import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Plus, Briefcase, Users, Clock, CheckCircle2, AlertCircle, Eye, Edit2 } from 'lucide-react';

const CompanyDashboard = () => {
  const { profile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/company/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusCounts = projects.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});

  const totalApplicants = projects.reduce((sum, p) => sum + (p.applicant_count || 0), 0);

  const statusColors = {
    pending: 'badge-warning',
    active: 'badge-success',
    paused: 'badge-neutral',
    filled: 'badge-primary',
    closed: 'badge-error',
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-dashboard">
      <Header />

      <div className="container-custom py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="heading-2 mb-2">Welcome back, {profile?.name}!</h1>
            <p className="text-slate-600">Manage your projects and review applicants</p>
          </div>
          
          {profile?.status === 'approved' && (
            <Link to="/company/projects/new" className="mt-4 md:mt-0">
              <Button className="btn-accent" data-testid="post-project-btn">
                <Plus className="h-4 w-4 mr-2" />
                Post New Project
              </Button>
            </Link>
          )}
        </div>

        {/* Company Status */}
        {profile?.status !== 'approved' && (
          <Card className="p-6 mb-8 border-amber-200 bg-amber-50" data-testid="status-alert">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-amber-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Company Verification Pending</h3>
                <p className="text-slate-600 text-sm">
                  Your company is being reviewed by our team. You'll be able to post projects once approved.
                  This typically takes 1-2 business days.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6" data-testid="stat-active-projects">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Briefcase className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{statusCounts.active || 0}</p>
                <p className="text-sm text-slate-500">Active Projects</p>
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="stat-total-applicants">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalApplicants}</p>
                <p className="text-sm text-slate-500">Total Applicants</p>
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="stat-pending-projects">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{statusCounts.pending || 0}</p>
                <p className="text-sm text-slate-500">Pending Review</p>
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="stat-filled-projects">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{statusCounts.filled || 0}</p>
                <p className="text-sm text-slate-500">Filled</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Projects List */}
        <Card className="p-6" data-testid="projects-list">
          <div className="flex items-center justify-between mb-6">
            <h2 className="heading-4">Your Projects</h2>
            <Link to="/company/projects" className="text-primary text-sm font-medium hover:underline">
              View all
            </Link>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse p-4 bg-slate-50 rounded-lg">
                  <div className="h-4 bg-slate-200 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-slate-200 rounded w-1/4"></div>
                </div>
              ))}
            </div>
          ) : projects.length > 0 ? (
            <div className="space-y-4">
              {projects.slice(0, 5).map((project) => (
                <div key={project.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid={`project-row-${project.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-medium text-slate-900 truncate">{project.title}</h3>
                      <span className={`${statusColors[project.status]} text-xs`}>{project.status}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span>{project.function}</span>
                      <span>{project.hours_per_week}h/week</span>
                      <span>{project.applicant_count || 0} applicants</span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Link to={`/company/projects/${project.id}`}>
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-medium text-slate-900 mb-2">No projects yet</h3>
              <p className="text-slate-500 mb-4">
                {profile?.status === 'approved' 
                  ? 'Post your first project to start finding talent'
                  : 'You can post projects once your company is verified'}
              </p>
              {profile?.status === 'approved' && (
                <Link to="/company/projects/new">
                  <Button className="btn-primary" data-testid="empty-post-project-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Post Your First Project
                  </Button>
                </Link>
              )}
            </div>
          )}
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default CompanyDashboard;
